import { FileUploadResponse } from "../../types/api/file";
import { useAlert } from "../../context/alert";
import { MediaResponse } from "../../types/api/common";
import { renderTruncatedText, truncateText } from '../../helpers/strings';
import { Stack, IconButton, Text } from "@primer/react";
import { DownloadIcon, TrashIcon, XIcon } from "@primer/octicons-react";

interface FileListComponentProps {
  media: FileUploadResponse | MediaResponse;
  isDeletingProcess: boolean
  isAllowDelete: boolean
  onFileDidDeleted: () => void;
  isUploading?: boolean;
  progress?: number;
  onCancel?: () => void;
}

const FileListComponent = ({
  media,
  isDeletingProcess,
  isAllowDelete,
  onFileDidDeleted,
  isUploading = false,
  progress = 0,
  onCancel,
}: FileListComponentProps) => {
  const { setError } = useAlert();

  const downloadFile = () => {
    const url = media.file
    if (url) {
      try {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = media.id;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        setError("Ошибка при скачивании файла");
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} Б`;
    } else if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} КБ`;
    } else {
      return `${Math.round(bytes / (1024 * 1024))} МБ`;
    }
  };

  const extractFileName = (url: string): string => {
    try {
      const urlPath = new URL(url).pathname;
      const fileName = urlPath.split('/').pop() || '';
      return truncateText(fileName, 32) || 'unknown_file';
    } catch {
      return 'unknown_file';
    }
  };

  const getFileName = () => {
    if ('title' in media && media.title) {
      return media.title;
    }

    if ('file' in media && media.file) {
      return extractFileName(media.file);
    }

    return 'unknown_file';
  };


  return (
    <Stack direction="vertical" gap="none" className="w-full rounded-lg border border-border">
      <Stack direction="horizontal" gap="normal" justify="space-between" className="w-full">
        <Stack gap="none" className="ml-4 mt-2 mb-2">
          <Text
            as="span"
            weight="normal"
            className="text-[15px] text-aloix-black"
          >
            {getFileName()}
          </Text>
          <Text
            as="span"
            weight="normal"
            className="text-[12px] text-aloix-grey"
          >
            {formatFileSize(media.size ?? 0)}
          </Text>
        </Stack>
        <div></div>
        <Stack direction="horizontal" gap="normal" justify="start" className="mt-2 mb-2 ml-2 mr-2">
          {!isUploading && (
            <IconButton
              icon={DownloadIcon}
              aria-label="Скачать"
              onClick={downloadFile}
            >
              Скачать
            </IconButton>
          )}

          {isUploading ? (
            <IconButton
              variant="invisible"
              icon={XIcon}
              aria-label="Отменить"
              onClick={onCancel}
            >
              Отменить
            </IconButton>
          ) : isAllowDelete && (
            <IconButton
              variant="danger"
              icon={TrashIcon}
              aria-label="Удалить"
              disabled={isDeletingProcess}
              onClick={onFileDidDeleted}
            >
              Удалить
            </IconButton>
          )}
        </Stack>
      </Stack>

      {isUploading && (
        <div className="px-4 py-3 mb-3">
          <div className="w-full bg-[#D1D9E0] rounded-full h-2">
            <div
              className="bg-[#1A7F37] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </Stack>
  );
};

export default FileListComponent;